'use client';

import { Globe, Mail } from 'lucide-react';

// Marketing footer shared by the logged-out homepage (via About.tsx) and
// /about. It SSRs, so the link list doubles as internal-link discovery for
// crawlers — /waitlist was a sitemap-only orphan with zero inbound links
// before this. The © year is inlined at build time (next.config `env`): a
// `new Date()` during render, even in a client component, postponed the whole
// page into the streamed Suspense hole under Cache Components, so nothing of
// the marketing page was in the prerendered HTML.
//
// `tagline` is the line under the name. The homepage keeps the default; /about
// passes its own, because that page is written around a different message.
const MarketingFooter = ({ tagline }: { tagline?: string }) => (
  <footer className="border-t border-ds-border-standard bg-background-primary">
    <div className="mx-auto max-w-7xl px-6 py-16">
      <div className="grid gap-12 lg:grid-cols-4">
        {/* Company Info */}
        <div className="lg:col-span-2">
          <div className="mb-6">
            <h3 className="text-2xl font-semibold text-textPrimary">Factiii</h3>
            <p className="mt-4 text-lg text-textSecondary">
              {tagline ??
                "The Internet's Truth Layer. Discover, verify, and share facts while keeping your privacy."}
            </p>
          </div>
          <div className="mb-8">
            <h4 className="mb-4 text-lg font-semibold text-textPrimary">
              Contact Info
            </h4>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Mail className="size-5 text-primary" />
                <a
                  href="mailto:support@factiii.com"
                  className="text-textSecondary transition-colors hover:text-primary"
                >
                  support@factiii.com
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Globe className="size-5 text-primary" />
                <span className="text-textSecondary">factiii.com</span>
              </div>
            </div>
          </div>
        </div>

        {/* Company Links */}
        <div>
          <h4 className="mb-6 text-lg font-semibold text-textPrimary">
            Company
          </h4>
          <ul className="space-y-4">
            <li>
              <a
                href="/about"
                className="text-textSecondary transition-colors hover:text-primary"
              >
                About
              </a>
            </li>
            <li>
              <a
                href="/contact"
                className="text-textSecondary transition-colors hover:text-primary"
              >
                Contact
              </a>
            </li>
            <li>
              <a
                href="mailto:support@factiii.com"
                className="text-textSecondary transition-colors hover:text-primary"
              >
                Support
              </a>
            </li>
            <li>
              <a
                href="/waitlist"
                className="text-textSecondary transition-colors hover:text-primary"
              >
                Get Early Access
              </a>
            </li>
          </ul>
        </div>

        {/* Explore Links */}
        <div>
          <h4 className="mb-6 text-lg font-semibold text-textPrimary">
            Explore
          </h4>
          <ul className="space-y-4">
            <li>
              <a
                href="/explore"
                className="text-textSecondary transition-colors hover:text-primary"
              >
                Explore
              </a>
            </li>
            <li>
              <a
                href="/trending"
                className="text-textSecondary transition-colors hover:text-primary"
              >
                Trending
              </a>
            </li>
            <li>
              <a
                href="/spaces"
                className="text-textSecondary transition-colors hover:text-primary"
              >
                Spaces
              </a>
            </li>
          </ul>
        </div>
      </div>

      {/* Bottom Section */}
      <div className="mt-16 border-t border-ds-border-standard pt-8">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-8">
            <p className="text-textSecondary">
              © {process.env.NEXT_PUBLIC_BUILD_YEAR} Factiii. All rights
              reserved.
            </p>
            <div className="flex gap-6">
              <a
                href="/privacy"
                className="text-textSecondary transition-colors hover:text-primary"
              >
                Privacy Policy
              </a>
              <a
                href="/terms"
                className="text-textSecondary transition-colors hover:text-primary"
              >
                Terms of Service
              </a>
              <a
                href="/cookies"
                className="text-textSecondary transition-colors hover:text-primary"
              >
                Cookie Policy
              </a>
            </div>
          </div>
          <p className="text-textSecondary">
            Humanity • Privacy • Transparency
          </p>
        </div>
      </div>
    </div>
  </footer>
);

export default MarketingFooter;
